import { useState, useRef, useEffect } from 'react'
import { Chart, registerables } from 'chart.js'
import { useIncome, useQuickIncome } from '../hooks/useData'
import { useAuth } from '../hooks/useAuth'
import { useStore } from '../store'
import { PageHeader, Card, CardTitle, GoldButton, Modal, Input, Select, fmt, Loading, Empty, colors, fonts } from '../components/UI'
import { useTheme } from '../lib/theme'
import { containsSensitiveData, SENSITIVE_DATA_ERROR } from '../lib/sensitiveDataCheck'
import { incomeOccursInMonth, getPayCycle, cyclesForPicker } from '../lib/payday'

Chart.register(...registerables)

const SOURCE_TYPES = [
  { value: 'salary', label: 'Salary' },
  { value: 'freelance', label: 'Freelance / self-employed' },
  { value: 'dividends', label: 'Dividends' },
  { value: 'rental', label: 'Rental' },
  { value: 'benefits', label: 'Benefits' },
  { value: 'other', label: 'Other' },
]

const INCOME_FREQUENCIES = [
  { value: 'monthly',        label: 'Every month' },
  { value: 'every_n_months', label: 'Every N months' },
  { value: 'quarterly',      label: 'Quarterly' },
  { value: 'annual',         label: 'Annually' },
]

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December']

const freqLabel = f => (INCOME_FREQUENCIES.find(x => x.value === f) || INCOME_FREQUENCIES[0]).label

const typeLabel = v => (SOURCE_TYPES.find(t => t.value === v) || { label: 'Other' }).label

function todayISO() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function currentMonthKey() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

// Build last-6-months totals from entries (recurring counts only in months it
// is scheduled; one-offs count in the month they were received).
function monthlyTotals(entries, monthsBack = 6) {
  const out = []
  const now = new Date()
  for (let i = monthsBack - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    const year = d.getFullYear()
    const monthIndex = d.getMonth()
    const total = entries.reduce((s, e) => {
      if (e.recurring) {
        return incomeOccursInMonth(e, year, monthIndex) ? s + (e.amount || 0) : s
      }
      return (e.received_on || '').slice(0, 7) === key ? s + (e.amount || 0) : s
    }, 0)
    out.push({ key, label: d.toLocaleString('en-GB', { month: 'short' }), total })
  }
  return out
}

// Monthly totals split by owner label, for the stacked per-person trend.
function monthlyTotalsByOwner(entries, monthsBack = 6) {
  const now = new Date()
  const months = []
  for (let i = monthsBack - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    months.push({ key: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, label: d.toLocaleString('en-GB', { month: 'short' }), year: d.getFullYear(), monthIndex: d.getMonth() })
  }
  const ownerSet = [...new Set(entries.map(e => e.owner_label || 'Joint'))]
  const owners = ownerSet.map(label => ({
    label,
    data: months.map(m => entries.reduce((s, e) => {
      if ((e.owner_label || 'Joint') !== label) return s
      const monthStart = new Date(m.year, m.monthIndex, 1)
      const monthEnd = new Date(m.year, m.monthIndex + 1, 0)
      if (e.started_at && new Date(e.started_at) > monthEnd) return s
      if (e.ended_at && new Date(e.ended_at) < monthStart) return s
      if (e.recurring) {
        return incomeOccursInMonth(e, m.year, m.monthIndex) ? s + (e.amount || 0) : s
      }
      return (e.received_on || '').slice(0, 7) === m.key ? s + (e.amount || 0) : s
    }, 0)),
  }))
  return { months, owners }
}

export default function IncomePage() {
  const { incomeEntries, loading, upsertIncomeEntry, deleteIncomeEntry, incomeForMonth, recurringMonthly } = useIncome()
  const { profile } = useAuth()
  const { themeId } = useTheme()
  const [showModal, setShowModal] = useState(false)
  const [editing, setEditing] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  const [importing, setImporting] = useState(false)

  // Per-person attribution. For dual-income households we offer the two real
  // labels from onboarding plus "Joint"; single households just get "Joint"
  // (effectively no tagging needed).
  const isDual = profile?.income_type === 'dual'
  const person1 = profile?.income_1_label || 'Me'
  const person2 = profile?.income_2_label || 'Partner'
  const OWNER_OPTIONS = isDual
    ? [{ value: person1, label: person1 }, { value: person2, label: person2 }, { value: 'Joint', label: 'Joint' }]
    : [{ value: 'Joint', label: 'Joint' }]
  const defaultOwner = isDual ? person1 : 'Joint'

  const [form, setForm] = useState({ source: '', type: 'salary', amount: '', recurring: true, frequency: 'monthly', interval_months: '', anchor_month: '', received_on: todayISO(), owner_label: defaultOwner })

  // Existing-user import: offer to seed entries from the per-person salary set
  // in onboarding, but only when there are no entries yet and the profile holds
  // income amounts. (monthly_income retired - we read the per-person fields.)
  const profileIncome = (profile?.income_1 || 0) + (profile?.income_2 || 0)
  const canImport = !loading && incomeEntries.length === 0 && profileIncome > 0

  async function importFromSettings() {
    setImporting(true)
    const rows = []
    if (profile?.income_type === 'dual') {
      if ((profile?.income_1 || 0) > 0) rows.push({ source: profile.income_1_label || 'Salary', type: 'salary', amount: profile.income_1, recurring: true, received_on: null, owner_label: profile.income_1_label || 'Me' })
      if ((profile?.income_2 || 0) > 0) rows.push({ source: profile.income_2_label || 'Partner salary', type: 'salary', amount: profile.income_2, recurring: true, received_on: null, owner_label: profile.income_2_label || 'Partner' })
    }
    if (rows.length === 0 && profileIncome > 0) {
      rows.push({ source: 'Salary', type: 'salary', amount: profileIncome, recurring: true, received_on: null, owner_label: 'Joint' })
    }
    for (const r of rows) await upsertIncomeEntry(r)
    setImporting(false)
  }

  const chartRef = useRef(null)
  const chartInstance = useRef(null)

  function setF(k, v) { setForm(f => ({ ...f, [k]: v })) }

  function openAdd() {
    setEditing(null)
    setForm({ source: '', type: 'salary', amount: '', recurring: true, frequency: 'monthly', interval_months: '', anchor_month: '', received_on: todayISO(), owner_label: defaultOwner, started_at: '', ended_at: '' })
    setShowModal(true)
  }

  function openEdit(e) {
    setEditing(e)
    setForm({
      source: e.source || '',
      type: e.type || 'other',
      amount: String(e.amount ?? ''),
      recurring: !!e.recurring,
      frequency: e.frequency || 'monthly',
      interval_months: e.interval_months ? String(e.interval_months) : '',
      anchor_month: e.anchor_month ? String(e.anchor_month) : '',
      received_on: e.received_on || todayISO(),
      owner_label: e.owner_label || defaultOwner,
      started_at: e.started_at || '',
      ended_at: e.ended_at || '',
    })
    setShowModal(true)
  }

  async function handleSave() {
    if (containsSensitiveData(form.source)) { setSaveError(SENSITIVE_DATA_ERROR); return }
    setSaveError(''); setSaving(true)
    const freq = form.recurring ? (form.frequency || 'monthly') : null
    const needsAnchor = ['quarterly', 'annual', 'every_n_months'].includes(freq)
    await upsertIncomeEntry({
      id: editing?.id,
      source: form.source,
      type: form.type,
      amount: parseFloat(form.amount) || 0,
      recurring: form.recurring,
      frequency: freq,
      interval_months: freq === 'every_n_months' ? (parseInt(form.interval_months) || null) : null,
      anchor_month: needsAnchor ? (parseInt(form.anchor_month) || null) : null,
      received_on: form.recurring ? null : (form.received_on || todayISO()),
      owner_label: form.owner_label || null,
      started_at: form.started_at || null,
      ended_at: form.ended_at || null,
    })
    setSaving(false)
    setShowModal(false)
  }

  const thisMonth = incomeForMonth(currentMonthKey())
  const totals = monthlyTotals(incomeEntries, 6)

  // Quick income (one-off) for current pay cycle
  const payDay = profile?.pay_day
  const shiftWeekends = profile?.pay_day_shift_weekends !== false
  const currentCycle = payDay ? getPayCycle(payDay, new Date(), shiftWeekends) : null
  const cycleKey = currentCycle?.startIso || ''
  const { cycleTotal: quickIncomeTotal } = useQuickIncome(cycleKey)

  // Total this period = recurring income + quick income
  const recurringThisPeriod = currentCycle ? incomeEntries
    .filter(e => e.recurring)
    .reduce((s, e) => {
      const start = new Date(currentCycle.startIso)
      return incomeOccursInMonth(e, start.getFullYear(), start.getMonth()) ? s + (parseFloat(e.amount) || 0) : s
    }, 0) : (parseFloat(thisMonth) || 0)
  const totalThisPeriod = (isNaN(recurringThisPeriod) ? 0 : recurringThisPeriod) + (isNaN(quickIncomeTotal) ? 0 : (quickIncomeTotal || 0))

  // Signup date - limit chart to from signup onwards
  const signupDate = profile?.created_at ? new Date(profile.created_at) : null
  const filteredTotals = signupDate
    ? totals.filter(t => new Date(t.key + '-01') >= new Date(signupDate.getFullYear(), signupDate.getMonth(), 1))
    : totals

  // Per-person recurring breakdown (dual households). Groups recurring income by
  // owner label so couples can see who contributes what to the household total.
  const perPerson = (() => {
    if (!isDual) return []
    const today = new Date()
    const groups = {}
    incomeEntries.filter(e => e.recurring).forEach(e => {
      if (e.started_at && new Date(e.started_at) > today) return
      if (e.ended_at && new Date(e.ended_at) < today) return
      const key = e.owner_label || 'Joint'
      groups[key] = (groups[key] || 0) + (e.amount || 0)
    })
    return Object.entries(groups).map(([label, amount]) => ({ label, amount }))
  })()

  // Trend chart
  useEffect(() => {
    if (!chartRef.current || incomeEntries.length === 0) return
    if (chartInstance.current) chartInstance.current.destroy()

    // Dual households get a stacked-by-person trend; single households keep the
    // simple single-series gold bar.
    const personColors = ['rgba(201,151,43,0.7)', 'rgba(74,143,232,0.6)', 'rgba(61,184,122,0.6)', 'rgba(123,104,238,0.6)']
    let chartData
    if (isDual) {
      const { months, owners } = monthlyTotalsByOwner(incomeEntries, 6)
      chartData = {
        labels: months.map(m => m.label),
        datasets: owners.map((o, i) => ({
          label: o.label,
          data: o.data,
          backgroundColor: personColors[i % personColors.length],
          borderWidth: 0,
          borderRadius: 2,
          stack: 'income',
        })),
      }
    } else {
      chartData = {
        labels: filteredTotals.map(t => t.label),
        datasets: [{
          data: filteredTotals.map(t => t.total),
          backgroundColor: 'rgba(201,151,43,0.5)',
          borderColor: colors.gold,
          borderWidth: 1,
          borderRadius: 3,
        }],
      }
    }

    chartInstance.current = new Chart(chartRef.current, {
      type: 'bar',
      data: chartData,
      options: {
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { display: isDual, labels: { color: colors.muted, font: { size: 11, family: fonts.body }, boxWidth: 10, padding: 12 } } },
        scales: {
          x: { stacked: isDual, grid: { display: false }, ticks: { color: colors.muted, font: { size: 11, family: fonts.body } } },
          y: { stacked: isDual, grid: { color: 'rgba(255,255,255,0.04)' }, ticks: { color: colors.muted, font: { size: 11 }, callback: v => '£' + (v >= 1000 ? (v / 1000).toFixed(1).replace(/\.0$/, '') + 'k' : v) } },
        },
      },
    })
    return () => { if (chartInstance.current) chartInstance.current.destroy() }
  }, [incomeEntries, isDual, themeId])

  if (loading) return <Loading />

  const today = new Date()
  const recurring = incomeEntries.filter(e => e.recurring && (!e.ended_at || new Date(e.ended_at) >= today))
  const oneOffs = incomeEntries.filter(e => !e.recurring)

  return (
    <div style={{ paddingBottom: '32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', padding: '18px 20px 4px', position: 'relative' }}>
        <div style={{ flex: 1, textAlign: 'center', paddingRight: '100px' }}>
          <div style={{ fontFamily: fonts.heading, fontWeight: 700, fontSize: '20px', color: colors.text }}>Income</div>
          <div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted, marginTop: '2px' }}>All your income sources</div>
        </div>
        <div style={{ position: 'absolute', right: '16px' }}><GoldButton small onClick={openAdd}>+ Add income</GoldButton></div>
      </div>

      <div style={{ padding: '16px' }}>
        {/* Existing-user import prompt */}
        {canImport && (
          <div style={{ background: colors.card, border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '16px', marginBottom: '14px' }}>
            <div style={{ fontSize: '16px', color: colors.text, fontFamily: fonts.body, lineHeight: 1.5, marginBottom: '12px' }}>
              We found income of <strong>{fmt(profileIncome)}/month</strong> from your setup. Import it as a recurring source to get started?
            </div>
            <GoldButton small onClick={importFromSettings}>{importing ? 'Importing…' : 'Import from settings'}</GoldButton>
          </div>
        )}
        {/* Summary metrics */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
          <div style={{ background: colors.card, border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '16px', marginBottom: '14px' }}>
            <div style={{ fontSize: '13px', color: colors.muted, letterSpacing: '0.1em', fontFamily: fonts.body, fontWeight: 600, marginBottom: '8px' }}>THIS PERIOD</div>
            <div style={{ fontSize: '32px', color: colors.text, fontWeight: 700, fontFamily: fonts.heading }}>{fmt(totalThisPeriod)}</div>
            {quickIncomeTotal > 0 && <div style={{ fontSize: '12px', color: colors.green, marginTop: '4px' }}>Incl. {fmt(quickIncomeTotal)} quick income</div>}
          </div>
          <div style={{ background: colors.card, border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '16px', marginBottom: '14px' }}>
            <div style={{ fontSize: '13px', color: colors.muted, letterSpacing: '0.1em', fontFamily: fonts.body, fontWeight: 600, marginBottom: '8px' }}>QUICK INCOME</div>
            <div style={{ fontSize: '32px', color: colors.green, fontWeight: 700, fontFamily: fonts.heading }}>{quickIncomeTotal > 0 ? fmt(quickIncomeTotal) : '£0'}</div>
            <div style={{ fontSize: '12px', color: colors.muted, marginTop: '4px' }}>one-off this period</div>
          </div>
        </div>

        {/* Per-person breakdown (dual-income households) */}
        {isDual && perPerson.length > 0 && (
          <div style={{ background: colors.card, border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '16px', marginBottom: '14px' }}>
            <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', color: colors.text, marginBottom: '12px' }}>WHO BRINGS IN WHAT</div>
            {perPerson.map(p => {
              const pct = recurringMonthly > 0 ? Math.round(p.amount / recurringMonthly * 100) : 0
              return (
                <div key={p.label} style={{ marginBottom: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <span style={{ fontSize: '16px', color: colors.text, fontFamily: fonts.body }}>{p.label}</span>
                    <span style={{ fontSize: '16px', color: colors.text, fontFamily: fonts.heading, fontWeight: 600 }}>{fmt(p.amount)} <span style={{ fontSize: '14px', color: colors.muted }}>· {pct}%</span></span>
                  </div>
                  <div style={{ height: '6px', background: 'rgba(255,255,255,0.06)', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: colors.gold }} />
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Trend */}
        <div style={{ background: colors.card, border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '16px', marginBottom: '14px' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', color: colors.text, marginBottom: '12px' }}>INCOME - LAST 6 MONTHS</div>
          {incomeEntries.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: colors.muted, fontFamily: fonts.body, fontSize: '16px' }}>Add income sources to see your trend</div>
          ) : (
            <div style={{ position: 'relative', height: '160px' }}>
              <canvas ref={chartRef} role="img" aria-label="Income over the last 6 months" />
            </div>
          )}
        </div>

        {/* Recurring sources */}
        <div style={{ background: colors.card, border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '16px', marginBottom: '14px' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', color: colors.text, marginBottom: '12px' }}>RECURRING INCOME</div>
          {recurring.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '28px 16px' }}><div style={{ fontSize: '13px', fontWeight: 500, color: `rgba(${colors.textRgb},0.82)`, marginBottom: '12px' }}>No recurring income yet</div><GoldButton small onClick={openAdd}>Add income</GoldButton></div>
          ) : (
            <div>
              {recurring.map(e => (
                <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${colors.border}` }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '14px', color: colors.text, fontFamily: fonts.body, fontWeight: 500 }}>{e.source || typeLabel(e.type)}</div>
                    <div style={{ fontSize: '14px', color: colors.muted, fontFamily: fonts.body, marginTop: '2px' }}>{typeLabel(e.type)} · {freqLabel(e.frequency || 'monthly')}{isDual && e.owner_label ? ` · ${e.owner_label}` : ''}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                    <span style={{ fontSize: '14px', color: colors.text, fontFamily: fonts.heading, fontWeight: 600 }}>{fmt(e.amount)}</span>
                    <button onClick={() => openEdit(e)} aria-label="Edit income" style={{ width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', color: colors.muted, cursor: 'pointer', borderRadius: '10px', flexShrink: 0 }}>
                      <i className="ti ti-edit" style={{ fontSize: '16px' }} />
                    </button>
                    <button onClick={() => deleteIncomeEntry(e.id)} aria-label="Delete income" style={{ width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', color: 'rgba(224,82,82,0.6)', cursor: 'pointer', borderRadius: '10px', flexShrink: 0 }}>
                      <i className="ti ti-trash" style={{ fontSize: '16px' }} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* One-off income */}
        {oneOffs.length > 0 && (
          <div style={{ background: colors.card, border: '1px solid rgba(255,255,255,0.06)', borderRadius: '16px', padding: '16px', marginBottom: '14px' }}>
            <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', color: colors.text, marginBottom: '12px' }}>ONE-OFF INCOME</div>
            <div>
              {oneOffs.map(e => (
                <div key={e.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${colors.border}` }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '14px', color: colors.text, fontFamily: fonts.body, fontWeight: 500 }}>{e.source || typeLabel(e.type)}</div>
                    <div style={{ fontSize: '14px', color: colors.muted, fontFamily: fonts.body, marginTop: '2px' }}>{typeLabel(e.type)} · {e.received_on ? new Date(e.received_on).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}{isDual && e.owner_label ? ` · ${e.owner_label}` : ''}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                    <span style={{ fontSize: '14px', color: colors.text, fontFamily: fonts.heading, fontWeight: 600 }}>{fmt(e.amount)}</span>
                    <button onClick={() => openEdit(e)} aria-label="Edit income" style={{ width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', color: colors.muted, cursor: 'pointer', borderRadius: '10px', flexShrink: 0 }}>
                      <i className="ti ti-edit" style={{ fontSize: '16px' }} />
                    </button>
                    <button onClick={() => deleteIncomeEntry(e.id)} aria-label="Delete income" style={{ width: '44px', height: '44px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', color: 'rgba(224,82,82,0.6)', cursor: 'pointer', borderRadius: '10px', flexShrink: 0 }}>
                      <i className="ti ti-trash" style={{ fontSize: '16px' }} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {showModal && (
        <Modal title={editing ? 'Edit income' : 'Add income'} onClose={() => setShowModal(false)}>
          <Input label="Source name" value={form.source} onChange={v => setF('source', v)} placeholder="e.g. Main salary, Etsy shop" />
          <Select label="Type" value={form.type} onChange={v => setF('type', v)} options={SOURCE_TYPES} />
          {isDual && (
            <Select label="Whose income?" value={form.owner_label} onChange={v => setF('owner_label', v)} options={OWNER_OPTIONS} />
          )}
          <Input label="Amount" type="number" prefix="£" value={form.amount} onChange={v => setF('amount', v)} placeholder="0" />

          {/* Recurring toggle */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '4px 0 14px' }}>
            <div>
              <div style={{ fontSize: '16px', color: colors.text, fontFamily: fonts.body, fontWeight: 500 }}>Recurring</div>
              <div style={{ fontSize: '14px', color: colors.muted, fontFamily: fonts.body, marginTop: '2px' }}>On for a regular income source; off for a one-off payment</div>
            </div>
            <button onClick={() => setF('recurring', !form.recurring)} aria-label="Toggle recurring" style={{ width: '44px', height: '24px', borderRadius: '12px', border: 'none', cursor: 'pointer', background: form.recurring ? colors.gold : 'rgba(255,255,255,0.15)', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
              <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '3px', left: form.recurring ? '23px' : '3px', transition: 'left 0.2s' }} />
            </button>
          </div>

          {/* Frequency - only shown when recurring */}
          {form.recurring && (
            <>
              <Select
                label="Frequency"
                value={form.frequency || 'monthly'}
                onChange={v => setF('frequency', v)}
                options={INCOME_FREQUENCIES}
              />
              {form.frequency === 'every_n_months' && (
                <Input
                  label="Every how many months?"
                  type="number"
                  value={form.interval_months}
                  onChange={v => setF('interval_months', v)}
                  placeholder="3"
                />
              )}
              {['quarterly', 'annual', 'every_n_months'].includes(form.frequency) && (
                <Select
                  label={form.frequency === 'annual' ? 'Which month?' : 'Starting month'}
                  value={form.anchor_month || ''}
                  onChange={v => setF('anchor_month', v)}
                  options={[{ value: '', label: 'Select a month…' }, ...MONTHS.map((m, i) => ({ value: String(i + 1), label: m }))]}
                />
              )}
            </>
          )}

          {!form.recurring && (
            <Input label="Date received" type="date" value={form.received_on} onChange={v => setF('received_on', v)} />
          )}

          {/* Effective dates - for recurring income */}
          {form.recurring && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <Input label="Started (optional)" type="date" value={form.started_at} onChange={v => setF('started_at', v)} />
              <Input label="Ended (optional)" type="date" value={form.ended_at} onChange={v => setF('ended_at', v)} placeholder="Leave blank if ongoing" />
            </div>
          )}

          {saveError && <div style={{ fontSize: '14px', color: colors.red, fontFamily: fonts.body, marginBottom: '6px', padding: '8px 12px', background: 'rgba(224,82,82,0.1)', borderRadius: '10px' }}>{saveError}</div>}
          <GoldButton onClick={handleSave} style={{ width: '100%', marginTop: '6px' }}>{saving ? 'Saving…' : (editing ? 'Save changes' : 'Add income')}</GoldButton>
        </Modal>
      )}
    </div>
  )
}
